"use client";

import { useMemo, useState } from "react";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";

const MARKS_ABOVE = [
  "̀",
  "́",
  "̂",
  "̃",
  "̄",
  "̅",
  "̆",
  "̇",
  "̈",
  "̉",
  "̊",
  "̋",
  "̌",
  "̍",
  "̎",
  "̏",
  "̐",
  "̑",
  "̒",
  "̓",
  "̔",
  "̽",
  "͊",
  "͋",
  "͌",
  "͐",
  "͑",
  "͒",
  "͗",
  "͛",
  "ͣ",
  "ͤ",
  "ͥ",
  "ͦ",
];

const MARKS_MIDDLE = ["̴", "̵", "̶", "̷", "̸"];

const MARKS_BELOW = [
  "̖",
  "̗",
  "̘",
  "̙",
  "̜",
  "̝",
  "̞",
  "̟",
  "̠",
  "̣",
  "̤",
  "̥",
  "̦",
  "̧",
  "̨",
  "̩",
  "̪",
  "̫",
  "̬",
  "̭",
  "̮",
  "̯",
  "̰",
  "̱",
  "̹",
  "̺",
  "̻",
  "̼",
  "ͅ",
  "͇",
  "͈",
  "͉",
  "͍",
  "͎",
  "͓",
  "͔",
  "͕",
  "͖",
  "͙",
  "͚",
];

const MIN_INTENSITY = 1;
const MAX_INTENSITY = 5;
const MIDDLE_MARK_THRESHOLD = 3;

// Small seeded PRNG (linear congruential generator) so each Regenerate click
// produces a new but stable variant for the current input and intensity.
const LCG_MULTIPLIER = 1_103_515_245;
const LCG_INCREMENT = 12_345;
const LCG_MODULUS = 2_147_483_648;

function createSeededRandom(seed: number): () => number {
  let state = (Math.abs(seed) + 1) % LCG_MODULUS;
  return () => {
    state = (state * LCG_MULTIPLIER + LCG_INCREMENT) % LCG_MODULUS;
    return state / LCG_MODULUS;
  };
}

function pickRandom(marks: string[], rng: () => number): string {
  return marks[Math.floor(rng() * marks.length)];
}

function randomMarks(marks: string[], max: number, rng: () => number): string {
  const count = 1 + Math.floor(rng() * max);
  let result = "";
  for (let i = 0; i < count; i += 1) {
    result += pickRandom(marks, rng);
  }
  return result;
}

function glitchChar(
  char: string,
  intensity: number,
  rng: () => number
): string {
  if (char.trim().length === 0) {
    return char;
  }
  let result = char + randomMarks(MARKS_ABOVE, intensity, rng);
  if (intensity >= MIDDLE_MARK_THRESHOLD) {
    result += pickRandom(MARKS_MIDDLE, rng);
  }
  result += randomMarks(MARKS_BELOW, intensity, rng);
  return result;
}

function glitchText(text: string, intensity: number, seed: number): string {
  const rng = createSeededRandom(seed);
  return [...text].map((char) => glitchChar(char, intensity, rng)).join("");
}

/** English strings. A translated content file overrides these through `ui`. */
export const GLITCH_TEXT_GENERATOR_STRINGS = {
  yourText: "Your text",
  inputPlaceholder: "Type or paste text to glitch",
  intensityPrefix: "Intensity: ",
  intensitySeparator: " / ",
  regenerate: "Regenerate",
  copied: "Copied!",
  copyResult: "Copy Result",
  glitchedText: "Glitched text",
  resultPlaceholder: "Your result will appear here",
} as const;

export default function GlitchTextGeneratorWidget() {
  const [input, setInput] = useState("");
  const [intensity, setIntensity] = useState(2);
  const [nonce, setNonce] = useState(0);
  const [copied, setCopied] = useState(false);

  const strings = useToolUi(GLITCH_TEXT_GENERATOR_STRINGS);

  const output = useMemo(
    () => glitchText(input, intensity, nonce),
    [input, intensity, nonce]
  );

  function handleCopy() {
    if (output.length === 0) {
      return;
    }
    writeToClipboard(output).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="glitch-input"
        >
          {strings.yourText}
        </label>
        <textarea
          className="min-h-[120px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="glitch-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.inputPlaceholder}
          value={input}
        />
      </div>

      {/* Intensity */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="glitch-intensity"
        >
          {strings.intensityPrefix}
          {intensity}
          {strings.intensitySeparator}
          {MAX_INTENSITY}
        </label>
        <input
          className="w-full accent-primary"
          id="glitch-intensity"
          max={MAX_INTENSITY}
          min={MIN_INTENSITY}
          onChange={(e) => setIntensity(Number(e.target.value))}
          step={1}
          type="range"
          value={intensity}
        />
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={input.length === 0}
          onClick={() => setNonce((n) => n + 1)}
          type="button"
        >
          {strings.regenerate}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copyResult}
        </button>
      </div>

      {/* Result */}
      <div className="space-y-2 rounded-lg border border-border bg-card p-5">
        <p className="font-medium text-foreground text-sm">
          {strings.glitchedText}
        </p>
        <p className="min-h-[3rem] whitespace-pre-wrap break-words py-3 text-base text-foreground leading-loose">
          {input.length > 0 ? output : strings.resultPlaceholder}
        </p>
      </div>
    </>
  );
}
